import type {
  ServerMessage,
  SnapshotRef,
  SourceFile,
} from 'ts-components/PiChat/protocol';
import {
  selectCanSend as selectChatCanSend,
  selectIsBusy as selectChatIsBusy,
  type PiChatState,
} from 'ts-components/PiChat/reducer';

export interface VersionsState {
  snapshots: SnapshotRef[];
  pointer: number;
  filesBySnapshotId: Record<string, SourceFile[]>;
  restoringSnapshotId: string | null;
}

export type VersionsAction =
  | { type: 'connecting' }
  | { type: 'server'; message: ServerMessage }
  | { type: 'restoreRequested'; snapshotId: string }
  | { type: 'restoreAborted' }
  | { type: 'filesLoaded'; snapshotId: string; files: SourceFile[] };

export const INITIAL_VERSIONS: VersionsState = {
  snapshots: [],
  pointer: -1,
  filesBySnapshotId: {},
  restoringSnapshotId: null,
};

const RESTORE_ERROR_CODES = new Set<string>([
  'busy',
  'state_unavailable',
  'cancelled',
  'restore_busy',
  'unsupported',
  'unknown_snapshot',
  'restore_failed',
  'prompt_failed',
  'session_create_failed',
  'conversation_invalid',
  'model_unavailable',
]);

export const endsRestore = (message: ServerMessage) =>
  message.type === 'snapshot.restored' ||
  (message.type === 'error' &&
    !message.clientMessageId &&
    RESTORE_ERROR_CODES.has(message.code));

const appendVersion = (
  state: VersionsState,
  snapshot: SnapshotRef
): VersionsState => {
  const snapshots = [
    ...state.snapshots
      .slice(0, state.pointer + 1)
      .filter(({ id }) => id !== snapshot.id),
    snapshot,
  ];
  return { ...state, snapshots, pointer: snapshots.length - 1 };
};

const reduceServerMessage = (
  state: VersionsState,
  message: ServerMessage
): VersionsState => {
  switch (message.type) {
    case 'session.ready': {
      const snapshots = message.snapshots ?? [];
      return {
        ...state,
        restoringSnapshotId: null,
        snapshots,
        pointer: message.snapshotPointer ?? snapshots.length - 1,
      };
    }
    case 'push.result':
    case 'version.added':
      return appendVersion(state, message.snapshot);
    case 'snapshot.restored': {
      const index = state.snapshots.findIndex(
        ({ id }) => id === message.snapshotId
      );
      return {
        ...state,
        restoringSnapshotId: null,
        ...(index === -1 ? {} : { pointer: index }),
      };
    }
    case 'error':
      return state.restoringSnapshotId && endsRestore(message)
        ? { ...state, restoringSnapshotId: null }
        : state;
    default:
      return state;
  }
};

export const versionsReducer = (
  state: VersionsState,
  action: VersionsAction
): VersionsState => {
  switch (action.type) {
    case 'connecting':
      return INITIAL_VERSIONS;
    case 'server':
      return reduceServerMessage(state, action.message);
    case 'restoreRequested':
      return { ...state, restoringSnapshotId: action.snapshotId };
    case 'restoreAborted':
      return state.restoringSnapshotId === null
        ? state
        : { ...state, restoringSnapshotId: null };
    case 'filesLoaded':
      if (!state.snapshots.some(({ id }) => id === action.snapshotId)) {
        return state;
      }
      return {
        ...state,
        filesBySnapshotId: {
          ...state.filesBySnapshotId,
          [action.snapshotId]: action.files,
        },
      };
    default:
      return state;
  }
};

export const versionFilesUrls = (
  message: ServerMessage
): { snapshotId: string; filesUrl: string }[] => {
  const refs: Pick<SnapshotRef, 'id' | 'filesUrl'>[] =
    message.type === 'session.ready'
      ? message.snapshots ?? []
      : message.type === 'push.result' || message.type === 'version.added'
        ? [message.snapshot]
        : message.type === 'snapshot.restored'
          ? [{ id: message.snapshotId, filesUrl: message.filesUrl }]
          : [];
  return refs.flatMap(({ id, filesUrl }) =>
    filesUrl ? [{ snapshotId: id, filesUrl }] : []
  );
};

export type VersionsPanelState = Pick<
  PiChatState,
  'connectionState' | 'isStreaming' | 'turnActive'
> &
  Pick<VersionsState, 'restoringSnapshotId' | 'snapshots' | 'pointer'>;

export const selectIsRestoring = (
  state: Pick<VersionsPanelState, 'connectionState' | 'restoringSnapshotId'>
) => state.connectionState === 'ready' && state.restoringSnapshotId !== null;

export const selectCurrentSnapshot = (
  state: Pick<VersionsState, 'snapshots' | 'pointer'>
): SnapshotRef | null => state.snapshots[state.pointer] ?? null;

export const selectCurrentFiles = (
  state: Pick<VersionsState, 'snapshots' | 'pointer' | 'filesBySnapshotId'>
): SourceFile[] | null => {
  const current = selectCurrentSnapshot(state);
  return current ? state.filesBySnapshotId[current.id] ?? null : null;
};

export const selectUndoTarget = (
  state: Pick<VersionsState, 'snapshots' | 'pointer'>
): SnapshotRef | null =>
  state.pointer > 0 ? state.snapshots[state.pointer - 1] ?? null : null;

export const selectRedoTarget = (
  state: Pick<VersionsState, 'snapshots' | 'pointer'>
): SnapshotRef | null =>
  state.pointer >= 0 ? state.snapshots[state.pointer + 1] ?? null : null;

export const selectIsBusy = (state: VersionsPanelState) =>
  selectChatIsBusy(state) || selectIsRestoring(state);

export const selectCanSend = (state: VersionsPanelState) =>
  selectChatCanSend(state) && !selectIsRestoring(state);

const canRestore = (state: VersionsPanelState) =>
  state.connectionState === 'ready' && !selectIsBusy(state);

export const selectCanUndo = (state: VersionsPanelState) =>
  canRestore(state) && selectUndoTarget(state) !== null;

export const selectCanRedo = (state: VersionsPanelState) =>
  canRestore(state) && selectRedoTarget(state) !== null;

export const selectVersionPosition = (
  state: Pick<VersionsState, 'snapshots' | 'pointer'>
): { current: number; total: number } | null =>
  state.snapshots.length > 1
    ? { current: state.pointer + 1, total: state.snapshots.length }
    : null;
